import { useBackgroundSync } from "@/hooks/useBackgroundSync";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { RefreshCw, Cloud, CloudOff, AlertCircle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export const SyncStatusIndicator = () => {
  const { pendingCount, isSyncing, syncAll, retryFailed } = useBackgroundSync();
  const isOnline = navigator.onLine;

  if (pendingCount === 0 && isOnline) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative gap-2"
        >
          {isSyncing ? (
            <RefreshCw className="h-4 w-4 animate-spin text-primary" />
          ) : !isOnline ? (
            <CloudOff className="h-4 w-4 text-muted-foreground" />
          ) : pendingCount > 0 ? (
            <AlertCircle className="h-4 w-4 text-orange-500" />
          ) : (
            <Cloud className="h-4 w-4 text-primary" />
          )}
          {pendingCount > 0 && (
            <Badge variant="secondary" className="h-5 min-w-5 px-1 text-xs">
              {pendingCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div>
            <h4 className="font-semibold mb-2">Background Sync</h4>
            {isSyncing ? (
              <p className="text-sm text-muted-foreground">
                Syncing {pendingCount} pending {pendingCount === 1 ? 'change' : 'changes'}...
              </p>
            ) : !isOnline ? (
              <p className="text-sm text-muted-foreground">
                You're offline. {pendingCount > 0 ? `${pendingCount} ${pendingCount === 1 ? 'change' : 'changes'} will sync when you're back online.` : 'Changes will be saved locally.'}
              </p>
            ) : pendingCount > 0 ? (
              <p className="text-sm text-muted-foreground">
                {pendingCount} {pendingCount === 1 ? 'change' : 'changes'} pending sync
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                All changes synced
              </p>
            )}
          </div>

          {isOnline && pendingCount > 0 && !isSyncing && (
            <div className="flex gap-2">
              <Button
                onClick={syncAll}
                size="sm"
                className="flex-1"
                disabled={isSyncing}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Sync Now
              </Button>
              <Button
                onClick={retryFailed}
                size="sm"
                variant="outline"
                className="flex-1"
              >
                Retry Failed
              </Button>
            </div>
          )}

          <div className="text-xs text-muted-foreground space-y-1">
            <p>✓ Draft forms are saved automatically</p>
            <p>✓ Changes sync when connection restored</p>
            <p>✓ No data loss during offline work</p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
